<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { Database, Plus, X } from 'lucide-svelte';

  import { selectedIndustryCentroids } from '$lib/stores/data';
  import { showDataSelectionModal } from '$lib/stores/modal';

  /**
   * The industries this selection is drawn from, in the panel that holds every
   * other criterion.
   *
   * # Why it moved out of the top bar
   *
   * It was a chip up beside the language switcher, and it did not belong there:
   * the bar holds things that act on the selection — share it, subtract from
   * it, abandon it — while this *is* part of the selection, and the first part
   * a customer picks. Sitting a hover-dropdown above the panel that shows every
   * other criterion made the industries look like a setting rather than a
   * filter.
   *
   * # Why the list is open rather than behind a dropdown
   *
   * A sidebar has the room, and what is in the selection is worth seeing
   * without asking. Removing one is then a click rather than a hover, a wait
   * and a click.
   */

  function open() {
    showDataSelectionModal.set(true);
  }

  function remove(id: string) {
    selectedIndustryCentroids.update((chosen) => {
      const left = new Map(chosen);
      left.delete(id);
      return left;
    });
  }

  $: entries = Array.from($selectedIndustryCentroids.entries());
</script>

<div>
  <div class="flex items-center justify-between gap-2 px-3 py-2.5">
    <div class="flex min-w-0 items-center gap-2">
      <Database class="filter-mark" />
      <span class="truncate text-xs font-medium text-gray-700">
        {$_('filters.industries.title')}{entries.length > 0 ? ` (${entries.length})` : ''}
      </span>
    </div>

    <button
      type="button"
      on:click={open}
      class="shrink-0 rounded-control border border-hairline px-2 py-1 text-[11px] font-medium text-gray-600 transition-colors hover:border-hairline-strong hover:text-gray-900"
    >
      {entries.length > 0 ? $_('filters.industries.chooseMore') : $_('filters.industries.choose')}
    </button>
  </div>

  {#if entries.length > 0}
    <div class="max-h-48 space-y-1 overflow-y-auto border-t border-hairline px-2 py-2">
      {#each entries as [id, name] (id)}
        <div
          class="flex items-center justify-between gap-2 rounded-control px-1.5 py-1 hover:bg-surface-canvas"
        >
          <!-- The same names, in a narrower panel still. Truncated here they
               are unreadable and there is no profile button beside them to
               open, so the tooltip is the only way to the full text. -->
          <span class="truncate text-xs text-gray-700" title={name}>{name}</span>
          <button
            type="button"
            on:click={() => remove(id)}
            class="shrink-0 text-gray-400 transition-colors hover:text-red-600"
            aria-label={`${$_('buttons.remove')}: ${name}`}
          >
            <X class="h-3.5 w-3.5" />
          </button>
        </div>
      {/each}
    </div>
  {:else}
    <button
      type="button"
      on:click={open}
      class="flex w-full items-center gap-2 border-t border-hairline px-3 py-2.5 text-left text-xs text-gray-500 transition-colors hover:bg-surface-canvas hover:text-gray-700"
    >
      <Plus class="h-3.5 w-3.5 text-gray-400" />
      {$_('filters.industries.none')}
    </button>
  {/if}
</div>
